import { type UseFormReturn, useForm } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useWatch } from 'react-hook-form';
import {
  generateBulkDeletionRequest,
  UserBulkDeletionFormValues,
  userBulkDeletionSchema,
} from '../logics';
import { useBulkDeletionUser } from './useBulkDeletionUser';

type Args = {
  onSuccess: () => void;
};

type IUseUserBulkDeletion = (args: Args) => {
  state: {
    methods: UseFormReturn<UserBulkDeletionFormValues>;
    registrationType: 'immediate' | 'scheduled';
    isConfirmDisabled: boolean;
  };
  actions: {
    handleSubmit: () => void;
  };
};

/**
 * CSV 一括削除モーダルの状態管理とアクションを提供するフック
 *
 * - RHF + Zod でフォームのバリデーションを管理する
 * - registrationType が 'scheduled' のときのみ日時フィールドを有効化する
 */
export const useUserBulkDeletion: IUseUserBulkDeletion = ({ onSuccess }) => {
  const { showToast } = useToast();
  const threeLetterCode = getThreeLetterCode() ?? '';

  const methods = useForm<UserBulkDeletionFormValues>({
    defaultValues: {
      registrationType: 'immediate',
      file: undefined,
      scheduledDate: undefined,
      scheduledTime: undefined,
    },
    resolver: zodResolver(userBulkDeletionSchema),
    mode: 'onChange',
  });

  const registrationType = useWatch({ control: methods.control, name: 'registrationType' });

  const { trigger } = useBulkDeletionUser(threeLetterCode, {
    onSuccess: () => {
      showToast({
        message:
          '利用者の一括削除を受け付けました。\n※すべての利用者の削除には最大10分程度かかる場合がございます。',
        status: 'success',
      });
      onSuccess();
    },
    onError: (error: AxiosError<{ errors?: string[] }>) => {
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        if (errors && errors.length > 0) {
          showToast({ message: errors[0], status: 'danger' });
          return;
        }
      }
      showToast({ message: '利用者の一括削除に失敗しました。', status: 'danger' });
    },
  });

  const handleSubmit = methods.handleSubmit(data => {
    trigger(generateBulkDeletionRequest(data));
  });

  return {
    state: {
      methods,
      registrationType,
      isConfirmDisabled: !methods.formState.isValid,
    },
    actions: {
      handleSubmit,
    },
  };
};
